import type { MapRef } from "@vis.gl/react-maplibre";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { useMapLibreLocationsAutoFit } from "@ryde/controls";
import {
  AREA_POLYGONS_FIT_BOUNDS_PADDING_PX,
  AreaPolygonEditLayer,
  AreaPolygonsLegend,
  AreaPolygonsViewerLayer,
  getAreaPolygonColor,
  IAreaPolygonEditHandle,
  IAreaPolygonItem,
} from "@ryde/layout";
import { useDynamicFormsFetch } from "@ryde/redux";
import {
  AreaApi,
  GlobalAppEventEnum,
  isTranslatableItem,
  Log,
  selectedTableRowsIdsAtomFamily,
  tableRowsAtomFamily,
  useDrawers,
  useHasPermissions,
} from "@ryde/shared";

import { AREAS_MAP_LEGEND_MIN_WIDTH } from "../areas.constants";
import {
  useAreaPolygonEditor,
  useAreaPolygonsFitLocations,
  useElementWiderThan,
} from "../hooks";
import { AreasGridMapStyled, AreasMapHostStyled } from "./areas.panels.styled";

export const AreasGridMap: FC = () => {
  const { t } = useTranslation();
  const { openEditFormDrawer } = useDrawers();
  const { fetchTableRows } = useDynamicFormsFetch("area");
  const { hasPermissions: canUpdateArea } = useHasPermissions({
    entity: "area",
    action: "update",
  });
  const selectedIds = useRecoilValue(selectedTableRowsIdsAtomFamily("area"));
  const tableRows = useRecoilValue(tableRowsAtomFamily("area"));
  const [areas, setAreas] = useState<IAreaPolygonItem[]>([]);
  const [hiddenIds, setHiddenIds] = useState<ReadonlySet<number>>(new Set());
  const [refreshKey, setRefreshKey] = useState(0);
  const editorRef = useRef<IAreaPolygonEditHandle>();
  const mapWrapperRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapRef>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  // Hide the floating legend when the map is too narrow to share with it — but
  // keep it while editing so its Save/Cancel stay reachable.
  const isMapWideEnough = useElementWiderThan(
    mapWrapperRef,
    AREAS_MAP_LEGEND_MIN_WIDTH,
  );

  // Re-fetch grid rows and polygons so the map reflects the latest data.
  const refreshAreas = useEvent((): void => {
    fetchTableRows();
    setRefreshKey(prev => prev + 1);
  });

  const { editingId, editingArea, isSaving, beginEdit, cancelEdit, saveEdit } =
    useAreaPolygonEditor({ areas, editorRef, onSaved: refreshAreas });

  useMapLibreLocationsAutoFit({
    mapRef,
    locations: useAreaPolygonsFitLocations({ areas, hiddenIds }),
    isMapLoaded,
    resizeEvent: GlobalAppEventEnum.RESIZE_AREAS_MAP,
    fitPadding: AREA_POLYGONS_FIT_BOUNDS_PADDING_PX,
  });

  // Unchecking the edited area's grid row cancels the in-map edit.
  useEffect(() => {
    if (editingId !== null && !selectedIds.includes(editingId)) {
      cancelEdit();
    }
  }, [editingId, selectedIds, cancelEdit]);

  const handleToggleArea = useEvent((id: number): void => {
    // The area being edited stays visible — its legend hide control is locked.
    if (id === editingId) {
      return;
    }

    setHiddenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  });

  const handleToggleAllVisible = useEvent((visible: boolean): void => {
    setHiddenIds(
      visible
        ? new Set()
        : new Set(
            areas
              // Never hide the area being edited, even on "hide all".
              .filter(
                ({ id, polygon }) =>
                  polygon?.features?.length && id !== editingId,
              )
              .map(({ id }) => id),
          ),
    );
  });

  const handleEditArea = useEvent(async (id: number): Promise<void> => {
    const res = await openEditFormDrawer({ entity: "area", id });

    if (res.success && res.data?.id) {
      refreshAreas();
    }
  });

  // Reset visibility whenever the selection changes — all areas start shown.
  useEffect(() => {
    setHiddenIds(new Set());
  }, [selectedIds]);

  useEffect(() => {
    if (!selectedIds?.length) {
      setAreas([]);
      return;
    }

    let isActive = true;

    void (async (): Promise<void> => {
      try {
        const result = await AreaApi.getPolygonsByIds(selectedIds);

        if (!isActive) {
          return;
        }

        if (!result?.success) {
          // Surface the backend's translatable message when present.
          const message = isTranslatableItem(result?.error)
            ? t(result.error.translationKey)
            : t("area.mapLoadError");
          setAreas([]);
          Log.errorAndToast(message, result?.error);
          return;
        }

        const polygonById = new Map(
          (result.data ?? []).map(({ id, polygon }) => [id, polygon]),
        );
        const selectedSet = new Set(selectedIds);
        const rows =
          (tableRows as unknown as { id: number; name: string }[]) ?? [];

        // Keep every selected area in the legend — even ones with no polygon;
        // each gets a distinct color, all polygons of one area share it.
        setAreas(
          rows
            .filter(row => selectedSet.has(row.id))
            .map((row, index) => ({
              id: row.id,
              name: row.name,
              color: getAreaPolygonColor(index),
              polygon: polygonById.get(row.id),
            })),
        );
      } catch (error) {
        if (isActive) {
          setAreas([]);
          Log.errorAndToast(t("area.mapLoadError"), error);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [selectedIds, tableRows, refreshKey, t]);

  return (
    <AreasGridMapStyled ref={mapWrapperRef}>
      <div className="map_scrollable_content">
        <AreasMapHostStyled mapRef={mapRef} onLoadedChange={setIsMapLoaded}>
          <AreaPolygonsViewerLayer
            areas={areas}
            hiddenIds={hiddenIds}
            editingId={editingId}
            onAreaClick={
              canUpdateArea && editingId === null ? beginEdit : undefined
            }
          />
          {editingArea && (
            <AreaPolygonEditLayer
              /* Remounts when a different area is picked up, which reloads the editor from its shape. */
              key={editingArea.id}
              color={editingArea.color}
              value={editingArea.polygon}
              handleRef={editorRef}
            />
          )}
        </AreasMapHostStyled>
        {isMapWideEnough && (
          <AreaPolygonsLegend
            areas={areas}
            hiddenIds={hiddenIds}
            editingArea={editingArea}
            isSaving={isSaving}
            onCancelEdit={cancelEdit}
            onSaveEdit={() => void saveEdit()}
            onToggle={handleToggleArea}
            onToggleAllVisible={handleToggleAllVisible}
            onEditArea={canUpdateArea ? handleEditArea : undefined}
          />
        )}
      </div>

      {!areas.length && (
        <div className="empty-state">{t("area.mapEmptySelectionHint")}</div>
      )}
    </AreasGridMapStyled>
  );
};
